<template>
  <div
    class="flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-md"
  >
    <template v-if="invoice">
      <PdfViewer
        ref="pdf-viewer"
        :url="renderUrl"
        :params="printSettings"
        :title="title"
        :back-route="{ name: 'invoice_info_route' }"
        :info-model="{
          type: 'invoice',
          id: $route.params.invoicing_id,
          dossier_id: invoice.dossier_id
        }"
        reverse-panel-position
      >
        <template #option-pane="{ dropDownMenuOption }">
          <TheInvoicingPrintLeftMenu
            v-if="isTierPayment"
            v-model:print-settings="printSettings"
            :headers="headers"
            :invoice="invoice"
            :menu-options="dropDownMenuOption"
            :covering-letters="covering_letters"
            @refresh-document="refreshDocument"
          />
          <PaymentPrintOptionsMenu v-model:print-settings="printSettings" />
        </template>
      </PdfViewer>
    </template>
    <div v-else class="flex flex-1 items-center justify-center bg-gray-700">
      <LoadingComponent />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Country } from '@/assets/enums'
import PdfViewer from '@c/Media/PdfViewer.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import TheInvoicingPrintLeftMenu from '@/views/invoicing/show/print/components/leftMenu/TheInvoicingPrintLeftMenu.vue'
import PaymentPrintOptionsMenu from '@c/baseComponents/modal/payment/PaymentPrintOptionsMenu.vue'

export default {
  name: 'PaymentPrintPage',
  components: {
    PaymentPrintOptionsMenu,
    TheInvoicingPrintLeftMenu,
    LoadingComponent,
    PdfViewer
  },
  data() {
    return {
      loading: false,
      invoice: null,
      headers: [],
      covering_letters: [],
      printSettings: {}
    }
  },
  computed: {
    ...mapGetters({
      default_country: 'metadata/getDefaultCountry',
      fh_settings: 'metadata/getFuneralHomeSettings',
      invoicing_categories: 'metadata/getInvoicingCategoriesMetadata'
    }),
    // Which document this page renders is carried by the route itself, not by a query
    // param the caller could get wrong: the two routes map one to one onto the two API
    // endpoints, so the viewer can never ask for one document while titling the other.
    isTierPayment() {
      return this.$route.meta.isTierPayment === true
    },
    renderUrl() {
      const base = `/invoicing/${this.$route.params.invoicing_id}/payment/${this.$route.params.payment_id}`

      return this.isTierPayment
        ? `${base}/render-third-party`
        : `${base}/render`
    },
    title() {
      return this.isTierPayment
        ? `${this.$t('enums.invoicing.docTypes.' + this.invoice?.doc_type)} - ${this.$t('global.third_payment')}`
        : this.$t('attributes.payments')
    }
  },
  watch: {
    'printSettings.show_remarks'() {
      this.refreshDocument()
    }
  },
  mounted() {
    this.loading = true
    // on fetch la facture.
    // avant de l'afficher dans le viewer, on fetch les headers pour attribuer le header par défaut.
    // quand les headers sont arrivé, on attribue la facture dans le data pour que le viewer s'affiche
    this.fetchInvoiceID({
      id: this.$route.params.invoicing_id,
      supplier_order_index: null
    }).then((response_invoice) => {
      this.fetchHeaderForInvoice({
        invoice_id: response_invoice.invoice.id
      })
        .then((response_header) => {
          const today = new Date()
          const cutoff = new Date('2025-07-01')

          this.headers = response_header.headers

          this.printSettings = {
            ...this.printSettings,
            show_remarks: false,
            header:
              this.headers.find((header) => header.favorite_invoice)?.id ??
              this.headers.find((header) => header.favorite)?.id ??
              null,
            normalized:
              [Country.FRANCE].includes(this.default_country) && today < cutoff,
            normalized_2025:
              [Country.FRANCE].includes(this.default_country) &&
              today >= cutoff,
            detailed: ![Country.FRANCE].includes(this.default_country)
          }

          if (response_invoice.invoice.person_id) {
            this.printSettings.language =
              response_invoice.invoice?.person?.language ?? this.$i18n?.locale
          } else {
            this.printSettings.language = this.$i18n?.locale
          }

          if (
            this.fh_settings?.[response_invoice.invoice.funeral_home_id]
              ?.invoicing_printing_settings?.[response_invoice.invoice.doc_type]
          ) {
            this.printSettings = {
              ...this.printSettings,
              ...this.fh_settings[response_invoice.invoice.funeral_home_id][
                'invoicing_printing_settings'
              ][response_invoice.invoice.doc_type]
            }
          } else {
            this.printSettings.normalized =
              [Country.FRANCE].includes(this.default_country) && today < cutoff
            this.printSettings.normalized_2025 =
              [Country.FRANCE].includes(this.default_country) && today >= cutoff
            this.printSettings.summary = ![Country.FRANCE].includes(
              this.default_country
            )
          }

          this.fixCategorySettings()

          this.invoice = response_invoice.invoice
        })
        .finally((_) => {
          this.loading = false
        })
    })
  },
  methods: {
    ...mapActions({
      fetchInvoiceID: 'invoicing/fetchInvoiceID',
      fetchHeaderForInvoice: 'invoicing/fetchHeaderForInvoice'
    }),
    fixCategorySettings() {
      this.invoicing_categories.map((invoicing_categories) => {
        this.printSettings.category_settings = {
          ...this.printSettings.category_settings,
          [invoicing_categories.id]: {
            mode:
              this.printSettings.category_settings?.[invoicing_categories.id]
                ?.mode ?? 'DTL'
          }
        }
      })
    },
    refreshDocument() {
      this.$refs['pdf-viewer'].reload()
    }
  }
}
</script>
